/**
 * FilePreviewDialog - opens a workspace asset in a modal for a faithful preview.
 *
 * Thin modal chrome around the shared AssetDocPreview renderer: given an asset uuid
 * it renders by mime type - .docx via docx-preview, images inline, everything else as
 * a download fallback. Used by the UserInteractionDrawer's per-option "Preview" button
 * (a choice that carries `documentAssetUuid`) and its "View full document" affordance.
 */

import { Cross1Icon, Dialog, IconSize } from '@protoss/ui';
import { useWorkspaceAssetMeta } from '../../../hooks/useWorkspaceAssetMeta';
import { AssetDocPreview } from './AssetDocPreview';

interface FilePreviewDialogProps {
  /** Asset to preview, or null when the dialog is closed. */
  assetUuid: string | null;
  /** Optional name for the header; falls back to the fetched original file name. */
  fileName?: string;
  /**
   * Optional heading text to scroll into view after a .docx renders (best-effort -
   * the first rendered line whose text matches; no-op when absent or unmatched).
   */
  scrollToHeading?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FilePreviewDialog({
  assetUuid,
  fileName,
  scrollToHeading,
  open,
  onOpenChange,
}: FilePreviewDialogProps) {
  // Only fetch the title meta while open - the body fetch is gated by AssetDocPreview.
  const meta = useWorkspaceAssetMeta(open ? assetUuid : null);
  const title = fileName ?? meta?.originalFileName ?? 'Preview';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content ai-file-preview" aria-describedby={undefined}>
          <Dialog.Title className="dialog-title ai-file-preview__title">{title}</Dialog.Title>
          <Dialog.Close className="dialog-close" aria-label="Close">
            <Cross1Icon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
          </Dialog.Close>
          <div className="ai-file-preview__body">
            <AssetDocPreview
              assetUuid={assetUuid}
              active={open}
              fileName={title}
              mode="full"
              heading={scrollToHeading}
            />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
